import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useMutation } from "@tanstack/react-query";
import {
  GeolocationService,
  GeolocationPermissionState,
} from "@/services/geolocationService";
import useSimpleLocalStorage from "./useSimpleLocalStorage";

const GEOLOCATION_PREFERENCE_KEY = "geolocation-enabled";

interface GeolocationState {
  userPreference: boolean;
  permissionState: PermissionState | null;
}

export interface UseGeolocationPermissionsReturn {
  isEnabled: boolean;
  permissionState: PermissionState | null;
  isSupported: boolean;
  isLoading: boolean;
  toggleGeolocation: () => Promise<void>;
}

export const useGeolocationPermissions =
  (): UseGeolocationPermissionsReturn => {
    const [userPreference, setUserPreference] = useSimpleLocalStorage(
      GEOLOCATION_PREFERENCE_KEY,
      false,
    );

    const [state, setState] = useState<GeolocationState>({
      userPreference,
      permissionState: null,
    });

    const [isInitializing, setIsInitializing] = useState(true);
    const isSupported = GeolocationService.isGeolocationSupported();
    const abortControllerRef = useRef<AbortController | null>(null);

    const isEnabled = useMemo(
      () => state.permissionState === "granted" && state.userPreference,
      [state.permissionState, state.userPreference],
    );

    const checkPermissions = useCallback(
      async (signal?: AbortSignal) => {
        if (!isSupported) {
          setState((prev) => ({
            ...prev,
            permissionState: null,
          }));
          setIsInitializing(false);
          return;
        }

        try {
          const permissionState = await GeolocationService.checkPermissions();

          if (signal?.aborted) return;

          setState((prev) => ({
            ...prev,
            permissionState,
          }));

          // Sync user preference with permission state
          if (permissionState === GeolocationPermissionState.DENIED) {
            setUserPreference(false);
            setState((prev) => ({
              ...prev,
              userPreference: false,
            }));
          }
        } catch (error) {
          if (signal?.aborted) return;

          // Fallback for browsers that don't support permissions API
          setState((prev) => ({
            ...prev,
            permissionState: null,
          }));
        } finally {
          if (!signal?.aborted) {
            setIsInitializing(false);
          }
        }
      },
      [isSupported, setUserPreference],
    );

    // Mutation for toggling geolocation
    const toggleMutation = useMutation({
      mutationFn: async () => {
        if (!isEnabled) {
          setUserPreference(true);
          setState((prev) => ({
            ...prev,
            userPreference: true,
          }));

          const position = await GeolocationService.getCurrentPosition({
            timeout: 5000,
          });
          return { enabled: true, position };
        } else {
          // Disabling
          setUserPreference(false);
          setState((prev) => ({
            ...prev,
            userPreference: false,
          }));
          return { enabled: false, position: null };
        }
      },
      onSuccess: async (result) => {
        if (result.enabled) {
          const permissionState = await GeolocationService.checkPermissions();
          setState((prev) => ({
            ...prev,
            permissionState,
          }));
        }
      },
      onError: async () => {
        const permissionState = await GeolocationService.checkPermissions();

        setState((prev) => ({
          ...prev,
          permissionState,
        }));

        if (permissionState === GeolocationPermissionState.DENIED) {
          // User denied permission - revert everything
          setUserPreference(false);
          setState((prev) => ({
            ...prev,
            userPreference: false,
          }));
        }
        // For other errors (timeout, GPS unavailable), keep preference enabled
        // since the user wanted to enable it and permission might be granted
      },
    });

    const toggleGeolocation = useCallback(async () => {
      if (!isSupported) return;
      await toggleMutation.mutateAsync();
    }, [isSupported, toggleMutation]);

    // Initialize on mount with cleanup
    useEffect(() => {
      const controller = new AbortController();
      abortControllerRef.current = controller;

      checkPermissions(controller.signal);

      return () => {
        controller.abort();
        abortControllerRef.current = null;
      };
    }, [checkPermissions]);

    useEffect(() => {
      setState((prev) => ({
        ...prev,
        userPreference,
      }));
    }, [userPreference]);

    return {
      isEnabled,
      permissionState: state.permissionState,
      isSupported,
      isLoading: isInitializing || toggleMutation.isPending,
      toggleGeolocation,
    };
  };
